import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { map } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useSearchParams } from 'react-router-dom'
import { ThreadShortDTO } from '../../../api/gen/base'
import { useInfiniteThreadListQuery } from '../../../api/hooks/useInfiniteThreadListQuery'
import { GuestMode } from '../../../components/GuestMode'
import { RequestInput } from '../../../components/RequestInput'
import { BlockWrapper } from '../../../components/Wrapper/BlockWrapper'
import { useWrapperStore } from '../../../store/storeWrapper'
import { RequestItem } from './components/RequestItem'

export const RequestPage = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const searchParamIsArchived = searchParams.get('is_archived') === 'true'

    const [tab, setTab] = useState(Number(searchParamIsArchived))
    const { setOpenSidebarType, isTempUser } = useWrapperStore((state) => state)

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isFetchedAfterMount,
    } = useInfiniteThreadListQuery({
        is_archived: Boolean(tab),
    })

    const requestsList = useMemo(
        () =>
            data?.pages.reduce<ThreadShortDTO[]>((acc, page) => acc.concat(page.objects), []) ?? [],
        [data],
    )

    const [infiniteRef] = useInfiniteScroll({
        loading: isFetching,
        hasNextPage,
        onLoadMore: fetchNextPage,
    })

    const handleClickTab = async (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue)
        if (newValue === 0) {
            setSearchParams({ is_archived: false })
        }
        if (newValue === 1) {
            setOpenSidebarType(null)
            setSearchParams({ is_archived: true })
        }
    }

    const {
        projectListActive,
        fetchModelDetail,
        currentAgentNameInDialog,
        currentAssistantIdDialog,
        fetchAssistantSubscriptions
    } = useWrapperStore((state) => state)

    useEffect(() => {
        setTab(Number(searchParamIsArchived))
    }, [searchParamIsArchived])

    useEffect(() => {
        if (currentAgentNameInDialog) {
            fetchModelDetail(currentAgentNameInDialog, currentAssistantIdDialog)
        }
    }, [currentAgentNameInDialog, currentAssistantIdDialog])

    useEffect(() => {
        fetchAssistantSubscriptions()
    }, [])

    return (
        <BlockWrapper
            sx={{ border: isTempUser ? '4px solid #006CFD' : 'none', boxSizing: 'border-box' }}>
            <GuestMode />
            <Grid
                container
                sx={{
                    position: 'relative',
                    height: `100%`,
                    flexDirection: 'column',
                    flexWrap: 'nowrap',
                    p: 2,
                }}
                size={12}>
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '16px',
                    }}>
                    <Tabs
                        value={tab}
                        onChange={handleClickTab}
                        sx={{
                            borderBottom: 'none',
                            p: 0,
                            mb: 2,
                        }}>
                        <Tab
                            variant="secondary"
                            disabled={isFetching}
                            label={
                                <Stack spacing={1} direction="row">
                                    <Typography>Все</Typography>
                                    {tab === 0 && (
                                        <Typography
                                            sx={{
                                                background: '#fff',
                                                borderRadius: '50px',
                                                height: '22px',
                                                width: '20px',
                                                fontSize: '12px',
                                            }}>
                                            {requestsList?.length}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            id="0"
                        />
                        <Tab
                            variant="secondary"
                            disabled={isFetching}
                            label={
                                <Stack spacing={1} direction="row">
                                    <Typography>Архивированные</Typography>
                                    {tab === 1 && (
                                        <Typography
                                            sx={{
                                                background: '#fff',
                                                borderRadius: '50px',
                                                height: '22px',
                                                width: '20px',
                                                fontSize: '12px',
                                            }}>
                                            {requestsList?.length}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            id="1"
                        />
                    </Tabs>
                </Stack>
                {isFetching && !isFetchedAfterMount && !isFetchingNextPage ? (
                    <>
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={60}
                            sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                        />
                        <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={60}
                            sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                        />
                        <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={60}
                            sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                        />
                        <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={60}
                            sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                        />
                        <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={60}
                            sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                        />
                        <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                    </>
                ) : (
                    <>
                        {requestsList.length > 0 ? (
                            <Stack
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'auto',
                                    flexGrow: 1,
                                    flexBasis: 0,
                                }}>
                                {map(requestsList, (request) => {
                                    return <RequestItem request={request} />
                                })}
                                {hasNextPage && (
                                    <Skeleton
                                        ref={infiniteRef}
                                        variant="rectangular"
                                        width="100%"
                                        height={60}
                                        sx={{
                                            mb: '4px',
                                            py: '12px',
                                            borderRadius: '24px',
                                            minHeight: '60px',
                                        }}
                                    />
                                )}
                            </Stack>
                        ) : (
                            <Typography variant="h5" sx={{ textAlign: 'center' }}>
                                Запросов не найдено
                            </Typography>
                        )}
                        {projectListActive && tab === 0 && <RequestInput />}
                    </>
                )}
            </Grid>
        </BlockWrapper>
    )
}
